import _, {isUndefined} from 'lodash';
import {concat, EMPTY, iif, merge, NEVER, Observable, Observer, OperatorFunction} from 'rxjs';
import {concatMap, exhaustMap, ignoreElements, tap} from 'rxjs/operators';

import {FrontWindow} from '../../libs/shared/core/src/helpers/browser/browserHelpers';
import {TaskMetadata} from '../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {fromIdleOrHiddenCallback} from '../../libs/shared/core/src/helpers/rx/fromIdleOrHiddenCallback';

export interface MapIdleOptions {
  /** The window in which to schedule the idle callbacks, if not the global window. */
  window?: FrontWindow;
  /** Force tasks to be processed after this timeout. Corresponds to the idle callback timeout. */
  timeout?: number;
  /** Cancel all pending tasks when this emits. */
  cancels$?: Observable<any>;
  /** Consider tasks backlogged after this timeout. For instrumentation purposes. */
  backlogTimeout?: number;
  /** Reports the number of backlogged tasks at the end of each idle callback. */
  backlogLengthObserver?: Observer<number>;
  /** Metadata for logging the task performance. */
  metadata?: TaskMetadata;
}

/** Like map, except the project function is called in an idle callback. For background processing. */
export function mapIdle<T, U>(
  project: (value: T, didTimeout: boolean) => U,
  options: MapIdleOptions = {},
): OperatorFunction<T, U> {
  const {window, timeout, cancels$ = NEVER, backlogTimeout, backlogLengthObserver, metadata = {}} = options;

  return (source$) => {
    const buffer: Array<{time: number; value: T}> = [];

    const flushBuffer = (): Observable<U> =>
      iif(
        () => buffer.length > 0,
        // If the buffer is non-empty, schedule an idle or hidden callback.
        fromIdleOrHiddenCallback(metadata, timeout, window).pipe(
          concatMap((deadline) =>
            concat(
              // Dequeue as many values as we can in the idle callback.
              new Observable<U>((subscriber) => {
                try {
                  while (buffer.length > 0) {
                    const {time, value} = buffer[0];
                    const didTimeout = !isUndefined(timeout) && performance.now() - time >= timeout;
                    const hasTimeRemaining = deadline.timeRemaining() > 0;

                    // Bail out if this task and thus subsequent tasks haven't timed out, and we have no more time remaining.
                    if (!didTimeout && !hasTimeRemaining) {
                      break;
                    }

                    buffer.shift();
                    subscriber.next(project(value, didTimeout));
                  }

                  subscriber.complete();

                  if (isUndefined(backlogTimeout) || !backlogLengthObserver) {
                    return;
                  }

                  const baseTime = performance.now();
                  const backlogLength = _(buffer)
                    .filter(({time}) => baseTime - time >= backlogTimeout)
                    .size();

                  backlogLengthObserver.next(backlogLength);
                } catch (error) {
                  subscriber.error(error);
                }
              }),
              flushBuffer(),
            ),
          ),
        ),
        // If the buffer is empty, return an empty observable.
        EMPTY,
      );

    return merge(
      source$.pipe(
        tap((value) => {
          // Enqueue the value.
          buffer.push({time: performance.now(), value});
        }),
        exhaustMap(() => flushBuffer()),
      ),
      cancels$.pipe(
        tap(() => {
          // Clear the buffer.
          buffer.splice(0, buffer.length);
        }),
        ignoreElements(),
      ),
    );
  };
}
